import { formatHumanName } from "@medplum/core";
import { Organization, Reference } from "@medplum/fhirtypes";

import { notifications } from "@mantine/notifications";

import { sendEmail } from "@/emails";
import encounterNoteTemplate from "@/emails/templates/encounterNoteTemplate";
import { inactiveUserAccountNotification, patientHasUser } from "@/utils/check-patient-user";
import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { EncounterEmailSenderType } from "./EncounterEmailSenderType";

export default async function EncounterEmailSender({
  medplum,
  patientId,
  questionnaireResponse,
  encounterLink,
  encounterNote,
}: EncounterEmailSenderType) {
  const patient = await medplum.readResource("Patient", patientId);
  if (!patient) {
    toast({
      title: "Error Sending Email to client",
      message: "Client not found",
      type: "error",
    });
    return;
  }
  const organization = await medplum.readReference(
    patient.managingOrganization as Reference<Organization>,
  );

  const encounterClientSummaryItem = questionnaireResponse?.item?.find(
    (item) => item.linkId === "encounterClientSummary",
  );
  // If the questionnaire response is undefined it means that it is a new encounter, so if we have a encounterNote we should send an email
  // If the encounter has a client summary item and the value is different from the encounter note we should send an email
  const shouldSendEmail =
    questionnaireResponse === undefined
      ? encounterNote !== ""
      : encounterClientSummaryItem?.answer?.[0]?.valueString !== encounterNote;

  const patientEmail = patient.telecom?.find((t) => t.system === "email")?.value;
  if (!patientEmail) {
    toast({
      title: "Error Sending Email to client",
      message: "Client email not found",
      type: "error",
    });
    return;
  }

  if (shouldSendEmail && patientHasUser(patient)) {
    const htmlContent = encounterNoteTemplate({
      clientName: formatHumanName(patient.name?.[0]) || "",
      organizationName: organization.name || "",
      encounterLink,
    });

    try {
      await sendEmail({
        medplum,
        email: patientEmail,
        subject: "Encounter Update",
        organizationName: organization.name || "",
        htmlContent,
      });
    } catch (error) {
      captureExceptionWithoutPhi("Failed to email the client about an encounter update", error);
      // for some reason the toast component is not working and it's breaking the build,
      // so we are using the notifications component instead
      notifications.show({
        title: "Error Sending Email to client",
        message:
          "We couldn't send the email to the client about the encounter update. If the problem persists, contact support.",
        color: "red.9",
        autoClose: false,
      });
    }
  } else if (!patientHasUser(patient)) {
    inactiveUserAccountNotification();
  }
}
